// ================================================================
// PROPRIETARY & CONFIDENTIAL — Mt Forge Inc.
// Copyright (c) 2026 Mt Forge. All rights reserved.
// Unauthorized copying, reverse engineering, or AI-assisted
// reproduction of this software is strictly prohibited.
// Protected under US Copyright Law (17 U.S.C.) and international
// treaties. Patent Pending. License: PROPRIETARY
// ================================================================
import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./AuthContext";
import Layout from "./components/Layout";
import LoginPage from "./pages/LoginPage";
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import PasswordChangePage from "./pages/PasswordChangePage";
import NDASignaturePage from "./pages/NDASignaturePage";
import AppTutorialPage from "./pages/AppTutorialPage";
import OverwatchPage from "./pages/OverwatchPage";
import DashboardPage from "./pages/DashboardPage";
import TaskBoardPage from "./pages/TaskBoardPage";
import MyTasksPage from "./pages/MyTasksPage";
import TeamTasksPage from "./pages/TeamTasksPage";
import SprintsPage from "./pages/SprintsPage";
import TeamDirectoryPage from "./pages/TeamDirectoryPage";
import RolesPermissionsPage from "./pages/RolesPermissionsPage";
import PerformancePage from "./pages/PerformancePage";
import ContactsPage from "./pages/ContactsPage";
import DealsPipelinePage from "./pages/DealsPipelinePage";
import ClientMeetingsPage from "./pages/ClientMeetingsPage";
import FollowUpsPage from "./pages/FollowUpsPage";
import ActivityFeedPage from "./pages/ActivityFeedPage";
import AnnouncementsPage from "./pages/AnnouncementsPage";
import MeetingNotesPage from "./pages/MeetingNotesPage";
import VisitLogPage from "./pages/VisitLogPage";
import VisitReportsPage from "./pages/VisitReportsPage";
import CallLogPage from "./pages/CallLogPage";
import CallRemindersPage from "./pages/CallRemindersPage";
import CallScriptsPage from "./pages/CallScriptsPage";
import ActiveProjectsPage from "./pages/ActiveProjectsPage";
import ProjectArchivePage from "./pages/ProjectArchivePage";
import MilestonesPage from "./pages/MilestonesPage";
import WeeklyReportPage from "./pages/WeeklyReportPage";
import KPIDashboardPage from "./pages/KPIDashboardPage";
import TimeTrackingPage from "./pages/TimeTrackingPage";
import ProfilePage from "./pages/ProfilePage";
import MeetingBriefPage from "./pages/MeetingBriefPage";
import AppHubPage from "./pages/AppHubPage";
import NDATrackerPage from "./pages/NDATrackerPage";
import SignLogPage from "./pages/SignLogPage";
import LicenseManagerPage from "./pages/LicenseManagerPage";
import PeoplePage from "./pages/PeoplePage";
import ValuationPage from "./pages/ValuationPage";
import PlatformAnalyticsPage from "./pages/PlatformAnalyticsPage";
import DevPage from "./pages/DevPage";
import SharedFoldersPage from "./pages/SharedFoldersPage";
import TeamChatPage from "./pages/TeamChatPage";
import LegalWatermark from "./components/LegalWatermark";
import "./App.css";

function OnboardingGate({ children }) {
  const { user, onboardingStep, completeOnboardingStep } = useAuth();
  if (!user) return children;
  if (onboardingStep === 'password') return <PasswordChangePage onComplete={() => completeOnboardingStep('password')} />;
  if (onboardingStep === 'nda') return <NDASignaturePage onComplete={() => completeOnboardingStep('nda')} />;
  if (onboardingStep === 'tutorial') return <AppTutorialPage onComplete={() => completeOnboardingStep('tutorial')} />;
  return children;
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading-screen"><div className="loading-spinner" /><p>Loading...</p></div>;
  if (!user) return <Navigate to="/login" />;
  return <OnboardingGate><Layout>{children}</Layout></OnboardingGate>;
}

function ForgotPasswordGuard() {
  const { user } = useAuth();
  return user ? <Navigate to="/" /> : <ForgotPasswordPage />;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordGuard />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/overwatch" element={<ProtectedRoute><OverwatchPage /></ProtectedRoute>} />
      <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/tasks" element={<ProtectedRoute><TaskBoardPage /></ProtectedRoute>} />
      <Route path="/my-tasks" element={<ProtectedRoute><MyTasksPage /></ProtectedRoute>} />
      <Route path="/team-tasks" element={<ProtectedRoute><TeamTasksPage /></ProtectedRoute>} />
      <Route path="/sprints" element={<ProtectedRoute><SprintsPage /></ProtectedRoute>} />
      <Route path="/team" element={<ProtectedRoute><TeamDirectoryPage /></ProtectedRoute>} />
      <Route path="/roles" element={<ProtectedRoute><RolesPermissionsPage /></ProtectedRoute>} />
      <Route path="/performance" element={<ProtectedRoute><PerformancePage /></ProtectedRoute>} />
      <Route path="/contacts" element={<ProtectedRoute><ContactsPage /></ProtectedRoute>} />
      <Route path="/deals" element={<ProtectedRoute><DealsPipelinePage /></ProtectedRoute>} />
      <Route path="/client-meetings" element={<ProtectedRoute><ClientMeetingsPage /></ProtectedRoute>} />
      <Route path="/follow-ups" element={<ProtectedRoute><FollowUpsPage /></ProtectedRoute>} />
      <Route path="/activity" element={<ProtectedRoute><ActivityFeedPage /></ProtectedRoute>} />
      <Route path="/announcements" element={<ProtectedRoute><AnnouncementsPage /></ProtectedRoute>} />
      <Route path="/meeting-notes" element={<ProtectedRoute><MeetingNotesPage /></ProtectedRoute>} />
      <Route path="/visit-log" element={<ProtectedRoute><VisitLogPage /></ProtectedRoute>} />
      <Route path="/visit-reports" element={<ProtectedRoute><VisitReportsPage /></ProtectedRoute>} />
      <Route path="/call-log" element={<ProtectedRoute><CallLogPage /></ProtectedRoute>} />
      <Route path="/call-reminders" element={<ProtectedRoute><CallRemindersPage /></ProtectedRoute>} />
      <Route path="/call-scripts" element={<ProtectedRoute><CallScriptsPage /></ProtectedRoute>} />
      <Route path="/projects" element={<ProtectedRoute><ActiveProjectsPage /></ProtectedRoute>} />
      <Route path="/archive" element={<ProtectedRoute><ProjectArchivePage /></ProtectedRoute>} />
      <Route path="/milestones" element={<ProtectedRoute><MilestonesPage /></ProtectedRoute>} />
      <Route path="/weekly-report" element={<ProtectedRoute><WeeklyReportPage /></ProtectedRoute>} />
      <Route path="/kpi" element={<ProtectedRoute><KPIDashboardPage /></ProtectedRoute>} />
      <Route path="/time-tracking" element={<ProtectedRoute><TimeTrackingPage /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
      <Route path="/meeting-brief" element={<ProtectedRoute><MeetingBriefPage /></ProtectedRoute>} />
      <Route path="/app-hub" element={<ProtectedRoute><AppHubPage /></ProtectedRoute>} />
      <Route path="/nda-tracker" element={<ProtectedRoute><NDATrackerPage /></ProtectedRoute>} />
      <Route path="/sign-log" element={<ProtectedRoute><SignLogPage /></ProtectedRoute>} />
      <Route path="/licenses" element={<ProtectedRoute><LicenseManagerPage /></ProtectedRoute>} />
      <Route path="/people" element={<ProtectedRoute><PeoplePage /></ProtectedRoute>} />
      <Route path="/valuation" element={<ProtectedRoute><ValuationPage /></ProtectedRoute>} />
      <Route path="/platform-analytics" element={<ProtectedRoute><PlatformAnalyticsPage /></ProtectedRoute>} />
      <Route path="/dev" element={<ProtectedRoute><DevPage /></ProtectedRoute>} />
      <Route path="/shared-folders" element={<ProtectedRoute><SharedFoldersPage /></ProtectedRoute>} />
      <Route path="/team-chat" element={<ProtectedRoute><TeamChatPage /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/dashboard" />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <LegalWatermark appName="MT Forge" />
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}